import { createStore } from "zustand";

import type {
	Gist,
	GistInfo,
	GistPopulated,
	Share,
} from "@vita-learning/types";

import {
	createGist,
	getGist,
	deleteGist,
	fetchGists,
	sortGistsByCompletion,
	toggleGist,
	updateGist,
	createManyGists,
	shareGist,
	createGistShareUrl,
} from "~/shared/services";
import { partition } from "~/shared/utils";

export interface GistsState {
	readonly data: GistPopulated[];
	readonly entity?: Gist | GistPopulated;
	readonly isPending: boolean;
}

interface GistRemoveOptions {
	/**
	 * If true, performs a soft delete.
	 */
	readonly soft?: boolean;
}

interface GistActions {
	readonly get: (id: string) => Promise<GistPopulated>;
	readonly refresh: (url: string) => Promise<GistPopulated[]>;
	readonly create: (gist: GistInfo | Gist) => Promise<GistPopulated>;
	readonly createMany: (gists: (GistInfo | Gist)[]) => Promise<GistPopulated[]>;
	readonly createShareUrl: (id: string) => Promise<string>;
	readonly update: (gist: Gist) => Promise<GistPopulated>;
	readonly toggle: (id: string) => Promise<GistPopulated>;
	readonly share: (id: string, share: Share) => Promise<GistPopulated>;
	readonly remove: (
		id: string,
		options?: GistRemoveOptions
	) => Promise<GistPopulated>;
	readonly set: (gist: Gist | ((prev: Gist) => Gist)) => void;
	readonly edit: (id: string, cb?: (id: string) => void) => void;
	readonly reset: () => void;
}

export type GistsStore = GistsState & GistActions;

const defaultState: GistsState = {
	data: [],
	isPending: false,
};

export const createGistsStore = (initialState: GistsState) =>
	createStore<GistsStore>((set, get) => ({
		...defaultState,
		...initialState,
		/**
		 * Refetch gists from the given URL
		 * @param {string} url The URL to fetch gists from
		 * @returns {Promise<GistPopulated[]>}
		 */
		refresh: async url => {
			try {
				set({ isPending: true });
				const gists = await fetchGists(url, { cache: "no-cache" });
				const sortedGists = gists.toSorted(sortGistsByCompletion);

				set({ data: sortedGists });
				return sortedGists;
			} finally {
				set({ isPending: false });
			}
		},
		/**
		 * Set the current gist entity
		 * @param {Gist | ((prev: Gist) => Gist)} gistOrUpdater The gist or updater function
		 */
		set: gistOrUpdater => {
			set(state => {
				if (!state.entity) return state;

				const nextEntity =
					typeof gistOrUpdater === "function"
						? gistOrUpdater(state.entity)
						: gistOrUpdater;
				return {
					...state,
					entity: nextEntity,
				};
			});
		},
		/**
		 * Create a new gist
		 * @param {GistInfo | Gist} gistInfo
		 * @returns {Promise<GistPopulated>}
		 */
		create: async gistInfo => {
			try {
				set({ isPending: true });
				const newGist = await createGist(gistInfo);

				set(state => ({
					...state,
					data: [...state.data, newGist].toSorted(sortGistsByCompletion),
				}));
				return newGist;
			} finally {
				set({ isPending: false });
			}
		},
		/**
		 * Create many gists
		 * @param {(GistInfo | Gist)[]} gists
		 * @returns {Promise<GistPopulated[]>}
		 */
		createMany: async gists => {
			try {
				set({ isPending: true });

				const newGists = await createManyGists(gists);

				set(state => ({ ...state, data: [...state.data, ...newGists] }));
				return newGists;
			} finally {
				set({ isPending: false });
			}
		},
		/**
		 * Create a share URL for a gist
		 * @param {string} id The gist ID
		 * @returns {Promise<string>}
		 */
		createShareUrl: async id => {
			try {
				set({ isPending: true });
				const { url } = await createGistShareUrl({ id });

				return url;
			} finally {
				set({ isPending: false });
			}
		},
		/**
		 * Get a gist by ID
		 * @param {string} id The gist ID
		 * @returns {Promise<GistPopulated>}
		 */
		get: async id => {
			try {
				set({ isPending: true });

				const gist = await getGist(id, { cache: "no-cache" });

				return gist;
			} finally {
				set({ isPending: false });
			}
		},
		/**
		 * Update a gist
		 * @param {GistInfo | Gist} gistInfo
		 * @returns {Promise<GistPopulated>}
		 */
		update: async gistInfo => {
			try {
				set({ isPending: true });
				const updatedGist = await updateGist(gistInfo);

				set(state => ({
					...state,
					entity: state.entity ? updatedGist : state.entity,
					data: state.data
						.map(g => (g.id !== updatedGist.id ? g : updatedGist))
						.toSorted(sortGistsByCompletion),
				}));
				return updatedGist;
			} finally {
				set({ isPending: false });
			}
		},
		/**
		 * Share a gist
		 * @param {string} id The gist ID
		 * @param {Share} share The share details
		 * @returns {Promise<GistPopulated>}
		 */
		share: async (id, share) => {
			try {
				set({ isPending: true });
				const sharedGist = await shareGist(id, share);

				set(state => ({
					...state,
					entity: state.entity ? sharedGist : state.entity,
					data: state.data
						.map(g => (g.id !== sharedGist.id ? g : sharedGist))
						.toSorted(sortGistsByCompletion),
				}));
				return sharedGist;
			} finally {
				set({ isPending: false });
			}
		},
		/**
		 * Toggle the reviewed status of a gist
		 * @param {string} id The gist ID
		 * @returns {Promise<GistPopulated>}
		 */
		toggle: async id => {
			try {
				set(state => ({
					...state,
					isPending: true,
					entity: state.entity
						? { ...state.entity, isReviewed: !state.entity.isReviewed }
						: state.entity,
					data: state.data
						.map(g => (g.id !== id ? g : { ...g, isReviewed: !g.isReviewed }))
						.toSorted(sortGistsByCompletion),
				}));

				await toggleGist(id);

				const toggledGist = get().data.find(g => g.id === id) as GistPopulated;

				return toggledGist;
			} finally {
				set({ isPending: false });
			}
		},
		/**
		 * Remove a gist
		 * @param {string} id The gist ID
		 * @returns {Promise<GistPopulated>}
		 */
		remove: async (id, { soft = false } = { soft: false }) => {
			try {
				set({ isPending: true });
				await deleteGist(id);

				const [[deletedGist], restOfGists] = partition(
					get().data,
					g => g.id === id
				);

				if (soft) return deletedGist as GistPopulated;

				set({ data: restOfGists, entity: undefined });
				return deletedGist as GistPopulated;
			} finally {
				set({ isPending: false });
			}
		},
		/**
		 * Edit a gist in the store
		 * @param {string} id The gist ID
		 * @param {((id: string) => void)} cb The callback function
		 */
		edit: (id, cb) => {
			cb?.(id);
			set(state => ({
				...state,
				entity: state.data.find(g => g.id === id),
			}));
		},
		/**
		 * Reset the store to its initial state
		 * @returns {void}
		 */
		reset: () => set({ ...initialState }),
	}));
